import React, { type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DateInput } from 'vl-common/src/components/DateInput';
import { Rule } from 'antd';
import { RegistrationFormItem, type RegistrationFormItemProps } from '../RegistrationFormItem';
import { useRegistrationFormInstance } from '../useSelfRegistrationFormInstance';

const dateOfBirthValidators: Rule[] = [
  {
    validator(_, value) {
      try {
        const parsedDoB = DateTime.fromFormat(value, 'yyyy-M-d');

        if (!parsedDoB.isValid) {
          return Promise.reject(new Error('Please enter a valid date'));
        }

        if (parsedDoB.diffNow().milliseconds > 0) {
          return Promise.reject(new Error('Date of birth must be in the past'));
        }
        if (parsedDoB.diffNow('years').years > -18) {
          return Promise.reject(new Error('You must be 18 to use our service'));
        }
        if (parsedDoB.diffNow('years').years < -110) {
          return Promise.reject(new Error('Please check that year of birth is correct'));
        }
      } catch (e) {
        console.log({ e });
        return Promise.reject(new Error('Please enter a valid date'));
      }

      return Promise.resolve();
    }
  }
];

export const DateOfBirthFormItem = ({
  inputProps,
  ...props
}: RegistrationFormItemProps & { inputProps?: Omit<ComponentProps<typeof DateInput>, 'label'> }) => {
  const { t } = useTranslation('common');
  const form = useRegistrationFormInstance();
  const fieldId = 'dob-day-input';

  return (
    <RegistrationFormItem
      label={t('DateOfBirth')}
      name="dob"
      htmlFor={fieldId}
      validateTrigger="onSubmit"
      getValueFromEvent={(val) => {
        return DateTime.fromFormat(val, 'yyyy-M-d').toFormat('yyyy-MM-dd');
      }}
      rules={dateOfBirthValidators}
      required
      {...props}
    >
      <DateInput
        label={t('DateOfBirth')}
        required
        hideLabel
        firstInputId={fieldId}
        onBlur={(v) => {
          try {
            const formattedISOdob = DateTime.fromFormat(v, 'yyyy-M-d').toFormat('yyyy-MM-dd');

            form.setFieldsValue({ dob: formattedISOdob });
          } catch {
            // do nothing
          }
        }}
        {...inputProps}
      />
    </RegistrationFormItem>
  );
};
